import { compactNumber, scoreTone } from "../utils/seo"

function Metric({ label, value, helper }) {
  return (
    <div className="metric-card pro-metric">
      <p className="metric-label">{label}</p>
      <div className="metric">{value}</div>
      {helper && <p className="muted">{helper}</p>}
    </div>
  )
}

function ScoreBar({ item }) {
  return (
    <div className="score-bar">
      <div className="score-bar-top">
        <strong>{item.label}</strong>
        <span className={`status ${item.status}`}>{item.score}/100</span>
      </div>
      <div className="bar-track">
        <span className={`bar-fill ${item.status}`} style={{ "--value": item.score }} />
      </div>
      <p className="muted">{item.summary}</p>
    </div>
  )
}

function Empty({ children }) {
  return <div className="empty-state">{children}</div>
}

export default function SeoIntelligencePanel({ domain, score = 0, insights = {} }) {
  const overview = insights.domainOverview || {}
  const scoreBreakdown = insights.scoreBreakdown || []
  const issueGroups = insights.issueGroups || []
  const issueCards = insights.issueCards || []
  const keywordOpportunities = insights.keywordOpportunities || []
  const topicClusters = insights.topicClusters || []
  const linkAnalytics = insights.linkAnalytics || {}
  const contentGaps = insights.contentGaps || {}
  const roadmap = insights.roadmap || []
  const topPages = insights.topPages || []

  const healthScore = overview.healthScore ?? score

  return (
    <div className="pro-suite">
      <section className="hero-panel pro-hero">
        <div>
          <p className="eyebrow">SEO Intelligence Suite</p>
          <h1>{domain || "Domain overview"}</h1>
          <p className="muted">
            {insights.summaryText ||
              "Run an audit to generate domain health, keyword, content, link and technical SEO insights."}
          </p>
          <div className="actions">
            <span className="pill high">Semrush-style report</span>
            <span className="pill medium">Crawl based estimates</span>
            <span className="pill low">v{insights.version || "3.0"}</span>
          </div>
        </div>
        <div className={`score-ring ${scoreTone(healthScore)}`} style={{ "--score": healthScore }}>
          {healthScore}
        </div>
      </section>

      <div className="grid">
        <Metric label="Authority score" value={overview.authorityScore ?? 0} helper="Proxy from links and page quality" />
        <Metric label="Visibility" value={`${overview.visibilityScore ?? 0}/100`} helper="Estimated organic visibility" />
        <Metric label="Traffic" value={compactNumber(overview.organicTrafficEstimate || 0)} helper="Estimated monthly potential" />
        <Metric label="Keywords" value={compactNumber(overview.organicKeywords || keywordOpportunities.length)} helper="Discovered targets" />
        <Metric label="Pages" value={compactNumber(overview.crawledPages || 0)} helper="Crawled URLs" />
        <Metric label="Internal links" value={compactNumber(overview.totalInternalLinks || 0)} helper="Discovered internal links" />
      </div>

      <div className="split-grid pro-section">
        <div className="card">
          <h2>Score Breakdown</h2>
          <div className="score-bars">
            {scoreBreakdown.map((item) => (
              <ScoreBar key={item.key} item={item} />
            ))}
            {scoreBreakdown.length === 0 && <Empty>No score breakdown yet.</Empty>}
          </div>
        </div>

        <div className="card">
          <h2>Priority Issues</h2>
          <div className="insight-list">
            {issueCards.slice(0, 7).map((issue) => (
              <div className="insight-item" key={issue.key}>
                <div className="actions">
                  <span className={`pill ${issue.priority}`}>{issue.priority}</span>
                  <strong>{issue.title}</strong>
                  <span className="muted">{issue.count} pages</span>
                </div>
                <p className="muted">{issue.category} - {issue.impact}</p>
                <p>{issue.fix}</p>
                {issue.affectedUrls?.length > 0 && (
                  <p className="mini-list">{issue.affectedUrls.map((url) => String(url)).join(" | ")}</p>
                )}
              </div>
            ))}
            {issueCards.length === 0 && <Empty>No priority issues detected.</Empty>}
          </div>
        </div>
      </div>

      <div className="card pro-section">
        <h2>Issue Categories</h2>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Health</th>
                <th>Total</th>
                <th>Critical</th>
                <th>High</th>
                <th>Main issue</th>
              </tr>
            </thead>
            <tbody>
              {issueGroups.map((group) => (
                <tr key={group.category}>
                  <td><strong>{group.category}</strong></td>
                  <td><span className={`status ${scoreTone(group.score)}`}>{group.score}/100</span></td>
                  <td>{group.count}</td>
                  <td>{group.critical}</td>
                  <td>{group.high}</td>
                  <td>{group.issues?.[0]?.title || "Clear"}</td>
                </tr>
              ))}
              {issueGroups.length === 0 && (
                <tr>
                  <td colSpan="6"><Empty>No issue categories found.</Empty></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="split-grid pro-section">
        <div className="card">
          <h2>Keyword Opportunities</h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Keyword</th>
                  <th>Intent</th>
                  <th>Volume</th>
                  <th>KD</th>
                  <th>Potential</th>
                  <th>Page type</th>
                </tr>
              </thead>
              <tbody>
                {keywordOpportunities.slice(0, 15).map((item) => (
                  <tr key={item.keyword}>
                    <td><strong>{item.keyword}</strong></td>
                    <td>{item.intent}</td>
                    <td>{compactNumber(item.volumeEstimate)}</td>
                    <td><span className="pill medium">{item.difficultyScore}</span></td>
                    <td><span className={`status ${scoreTone(item.potential)}`}>{item.potential}/100</span></td>
                    <td>{item.recommendedPageType}</td>
                  </tr>
                ))}
                {keywordOpportunities.length === 0 && (
                  <tr>
                    <td colSpan="6"><Empty>No keyword opportunities yet.</Empty></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h2>Topic Clusters</h2>
          <div className="cluster-list">
            {topicClusters.slice(0, 8).map((cluster) => (
              <div className="cluster-card" key={cluster.topic}>
                <div className="score-bar-top">
                  <strong>{cluster.topic}</strong>
                  <span className={`status ${scoreTone(cluster.potential)}`}>{cluster.potential}/100</span>
                </div>
                <p className="muted">{(cluster.keywords || []).slice(0, 8).join(", ")}</p>
              </div>
            ))}
            {topicClusters.length === 0 && <Empty>No clusters yet.</Empty>}
          </div>
        </div>
      </div>

      <div className="split-grid pro-section">
        <div className="card">
          <h2>Backlink & Link Analytics</h2>
          <div className="grid tight-grid">
            <Metric label="Authority" value={linkAnalytics.authorityScore || 0} helper="Internal/referring proxy" />
            <Metric label="Referring domains" value={linkAnalytics.referringDomains || 0} helper="Saved link data" />
            <Metric label="Avg internal links" value={linkAnalytics.averageInternalLinks || 0} helper="Per crawled page" />
          </div>
          <h3>Weak internal link pages</h3>
          <div className="insight-list">
            {(linkAnalytics.weakPages || []).slice(0, 6).map((page) => (
              <div className="insight-item" key={page.url}>
                <strong>{page.title}</strong>
                <p className="muted">{page.url}</p>
                <p>{page.internalLinks} internal links found. Add contextual links from relevant pages.</p>
              </div>
            ))}
            {(linkAnalytics.weakPages || []).length === 0 && <Empty>No weak internal-link pages detected.</Empty>}
          </div>
        </div>

        <div className="card">
          <h2>Content Gap Analyzer</h2>
          <div className="insight-list">
            {(contentGaps.thinPages || []).slice(0, 5).map((page) => (
              <div className="insight-item" key={page.url}>
                <span className="pill high">content gap</span>
                <strong>{page.title}</strong>
                <p className="muted">{page.words} words - {page.url}</p>
                <p>{page.action}</p>
              </div>
            ))}
            {(contentGaps.missingIntentPages || []).slice(0, 5).map((item) => (
              <div className="insight-item" key={item.keyword}>
                <span className="pill medium">{item.intent}</span>
                <strong>{item.keyword}</strong>
                <p>{item.action}</p>
              </div>
            ))}
            {!(contentGaps.thinPages || []).length && !(contentGaps.missingIntentPages || []).length && (
              <Empty>No content gaps detected.</Empty>
            )}
          </div>
        </div>
      </div>

      <div className="split-grid pro-section">
        <div className="card">
          <h2>Execution Roadmap</h2>
          <div className="roadmap">
            {roadmap.map((item) => (
              <div className="roadmap-item" key={`${item.phase}-${item.title}`}>
                <div>
                  <div className="actions">
                    <span className={`pill ${item.priority}`}>{item.phase}</span>
                    <strong>{item.title}</strong>
                    <span className="muted">Effort: {item.effort}</span>
                  </div>
                  <p className="muted">{item.impact}</p>
                  <p>{item.action}</p>
                </div>
              </div>
            ))}
            {roadmap.length === 0 && <Empty>No roadmap needed.</Empty>}
          </div>
        </div>

        <div className="card">
          <h2>Top Pages To Fix</h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Score</th>
                  <th>Opportunity</th>
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                {topPages.slice(0, 12).map((page) => (
                  <tr key={page.url}>
                    <td>
                      <strong>{page.title}</strong>
                      <p className="muted">{page.path || page.url}</p>
                    </td>
                    <td><span className={`status ${scoreTone(page.score)}`}>{page.score}/100</span></td>
                    <td>{page.opportunityScore}/100</td>
                    <td>{(page.issues || []).join(", ") || "OK"}</td>
                  </tr>
                ))}
                {topPages.length === 0 && (
                  <tr>
                    <td colSpan="4"><Empty>No page data yet.</Empty></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {insights.notes?.length > 0 && (
        <div className="card pro-section">
          <h2>Data Notes</h2>
          {insights.notes.map((note) => (
            <p className="muted" key={note}>{note}</p>
          ))}
        </div>
      )}
    </div>
  )
}
